import { useParams } from "react-router-dom";
import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { getVariation } from "@habitify/shared/variations";
import { ensureSession } from "../lib/session";
import { fireCapiOnce } from "../lib/capi";
import { getAppHandoffUrl } from "../lib/appHandoff";

/**
 * Per-slug dispatcher. Each variation lives in its own folder so a
 * designer can hack on one funnel without touching another. The
 * shared survey engine + paywall + tracking live in /components and
 * /lib so every variation gets them for free.
 *
 * If the slug doesn't resolve we send the user to the App Store /
 * Google Play (mobile) or habitify.me (desktop) — never back to "/",
 * which is now admin-gated.
 *
 * ── Code-splitting ──────────────────────────────────────────────────
 * Each funnel orchestrator is a separate lazy chunk. The dispatcher
 * only ever renders ONE funnel per visit (picked by the URL slug), so
 * statically importing all three forced every lander to download and
 * parse *all four* funnels' screens (the ~664 KB main chunk) before the
 * first paint. Lazy-loading means a paid visitor pulls only the funnel
 * they actually see — and since the slug is known synchronously from the
 * URL, the import fires on first render (not on interaction), so the
 * chunk downloads in parallel with React bootstrapping. Vite emits a
 * modulepreload for it, so there's no waterfall.
 */
const VariationA = lazy(() =>
  import("./variation-a").then((m) => ({ default: m.VariationA }))
);
const HyperfocusProtocol = lazy(() =>
  import("./hyperfocus-protocol").then((m) => ({
    default: m.HyperfocusProtocol,
  }))
);
const HyperfocusProtocolV2 = lazy(() =>
  import("./hyperfocus-protocol-v2").then((m) => ({
    default: m.HyperfocusProtocolV2,
  }))
);

/**
 * Placeholder shown while the funnel chunk loads. The chunk is
 * same-origin + small (tens of KB gz), so this is usually imperceptible;
 * it's a silent canvas-coloured screen (no spinner/text) so there's no
 * flash of "loading" chrome before the first survey screen paints.
 */
function FunnelBoot() {
  return <div className="min-h-[100dvh] bg-bg-canvas" aria-hidden />;
}

export function VariationApp() {
  const { slug } = useParams<{ slug: string }>();
  const variation = slug ? getVariation(slug) : undefined;

  useEffect(() => {
    // Eagerly mint a session as soon as the variation mounts — every
    // downstream tracking call relies on it.
    if (slug && variation) {
      void ensureSession(slug);
      // Meta ViewContent — `fireCapiOnce` (not `fireCapi`) so React
      // strict-mode's double-mount, browser back/forward, and any
      // future re-mount of <VariationApp /> share the same event_id.
      // Without this, every remount minted a fresh UUID and Meta
      // counted multiple ViewContents for the same logical view —
      // inflating frequency and tanking the dedup-keys-match metric
      // in Events Manager.
      fireCapiOnce(`view_content:${slug}`, {
        eventName: "ViewContent",
        contentName: slug,
        contentCategory: "funnel",
      });
    }
  }, [slug, variation]);

  if (!slug || !variation || variation.status === "paused") {
    // Bad slug or paused funnel → drop the user where they can still
    // grab the app (iOS / Android store) or the web home. We don't
    // redirect to "/" anymore — that route is now admin-gated.
    if (typeof window !== "undefined") {
      window.location.replace(getAppHandoffUrl());
    }
    return null;
  }

  let funnel: ReactNode;
  switch (slug) {
    case "productivity-archetype":
      funnel = <VariationA variation={variation} />;
      break;
    case "hyperfocus-protocol":
      funnel = <HyperfocusProtocol variation={variation} />;
      break;
    case "hyperfocus-protocol-v2":
      funnel = <HyperfocusProtocolV2 variation={variation} />;
      break;
    case "hyperfocus-protocol-question-first":
      // Same funnel as v2 — only the entry differs (opens on the
      // gender question instead of the intro splash).
      funnel = (
        <HyperfocusProtocolV2 variation={variation} entryMode="question" />
      );
      break;
    default:
      // Fallback for variations registered in shared/ but not yet
      // given a bespoke implementation — render them through the
      // archetype funnel so they're still functional.
      funnel = <VariationA variation={variation} />;
  }

  return <Suspense fallback={<FunnelBoot />}>{funnel}</Suspense>;
}
